import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { RefreshCw } from "lucide-react";
import { useDraftChanges } from "@/api/queries/repoLifecycle";

// #293 (UC-3/UC-4): „Änderungen ansehen" als DIALOG — der Admin sieht WAS sich ändert
// (Dateien + Prüfstatus), ohne je einen Branch/Stand in einem Panel zu wechseln. Der
// Dialog ist reine Fach-Antwort auf „was würde mit der Freigabe passieren?"; wer
// editieren will, nutzt die Werkbank.
export function DraftChangesDialog({
  open,
  onOpenChange,
  repoId,
  branch,
  title,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lifecycle-Koordinaten (Server-Zusage) — nie aus Anzeige-Werten geraten. */
  repoId: string;
  branch: string;
  /** Fach-Titel der Änderung (für die Dialog-Überschrift). */
  title: string;
}) {
  const changes = useDraftChanges(open ? repoId : undefined, open ? branch : null);
  const c = changes.data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="draft-changes-dialog">
        <DialogHeader>
          <DialogTitle>Änderungen: {title}</DialogTitle>
          <DialogDescription>
            Das würde mit der Freigabe live gehen — nichts davon ist bis dahin wirksam.
          </DialogDescription>
        </DialogHeader>

        {changes.isLoading && (
          <div className="flex items-center gap-2 py-4 text-sm text-text3">
            <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Änderungen…
          </div>
        )}
        {changes.isError && (
          <p className="text-[12.5px] text-danger">Änderungen konnten nicht geladen werden.</p>
        )}
        {c && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-[12.5px]">
              <Badge variant={c.schemaOk ? "enrolled" : "destructive"}>
                {c.schemaOk ? "Prüfung bestanden" : "Prüfung fehlgeschlagen"}
              </Badge>
              <span className="text-text3">
                {c.files.length === 1 ? "1 geänderte Datei" : `${c.files.length} geänderte Dateien`}
              </span>
            </div>
            {!c.schemaOk && c.schemaIssues.length > 0 && (
              <ul className="space-y-1 rounded-md border border-danger/40 bg-danger/5 p-2 text-[12px]" data-testid="changes-issues">
                {c.schemaIssues.map((i, idx) => (
                  <li key={idx}>
                    <span className="mono">{i.path}</span>: {i.message}
                  </li>
                ))}
              </ul>
            )}
            <ul className="max-h-72 space-y-1 overflow-y-auto" data-testid="changes-files">
              {c.files.map((f) => (
                <li key={f.filename} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-[12.5px]">
                  <Badge variant="outline">{f.status}</Badge>
                  <span className="mono truncate">{f.filename}</span>
                  <span className="ml-auto whitespace-nowrap text-[11.5px] text-text3">
                    +{f.additions} / −{f.deletions}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
